import axios, { type AxiosInstance, type AxiosError, type AxiosRequestConfig } from 'axios';
import { EXAM_BROWSER_HEADER, EXAM_SESSION_REPLACED_EVENT, getExamBrowserKey } from './examBrowserSession';


export const BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');
const REQUEST_TIMEOUT = 60000;

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function setAuthToken(token: string | null): void {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
}

export function clearAuthToken(): void {
  delete apiClient.defaults.headers.common['Authorization'];
}

apiClient.interceptors.request.use(
  (config) => {
    if (/^\/(quiz|proctoring)(\/|$)/.test(config.url || '')) {
      config.headers[EXAM_BROWSER_HEADER] = getExamBrowserKey();
    }
    const token = localStorage.getItem('refreshToken');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const requestUrl = error.config?.url || '';
    const conflict = error.response?.data as { code?: string; message?: string; activeIp?: string } | undefined;
    if (conflict?.code === 'EXAM_SESSION_REPLACED' && !requestUrl.includes('/initialize-exam')) {
      window.dispatchEvent(new CustomEvent(EXAM_SESSION_REPLACED_EVENT, { detail: conflict }));
    }
    const isAuthLoginRequest =
      requestUrl.includes('/auth/login') || requestUrl.endsWith('/login');

    if (error.response?.status === 401 && !isAuthLoginRequest) {
      const isExamRoute = window.location.pathname.startsWith('/exam');
      localStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
      if (!isExamRoute) {
        window.location.href = '/admin';
      }
    }
    return Promise.reject(error);
  }
);

export type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue };

export interface ApiError {
  message?: string;
  errors?: string[];
}

interface ApiErrorResponse {
  message?: string;
  status?: number;
  errors?: string[];
}

interface ApiEnvelope<T> {
  data: T;
  success?: boolean;
  status?: number;
  message?: string;
  errors?: string[];
}

function isApiEnvelope<T>(value: unknown): value is ApiEnvelope<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    ('success' in value || 'status' in value || 'errors' in value || 'message' in value)
  );
}

function unwrapApiResponse<T>(payload: T | ApiEnvelope<T>): T {
  return isApiEnvelope<T>(payload) ? payload.data : payload;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<ApiErrorResponse | string>(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data.trim()) {
      return data;
    }
    if (data && typeof data === 'object') {
      if (data.message?.trim()) {
        return data.message;
      }
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors.join(', ');
      }
    }
    if (error.message) {
      return error.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export async function apiGet<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T | ApiEnvelope<T>>(endpoint, { params });
  return unwrapApiResponse(response.data);
}

export async function apiPost<T>(
  endpoint: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<T | ApiEnvelope<T>>(endpoint, data, config);
  return unwrapApiResponse(response.data);
}

export async function apiPut<T>(
  endpoint: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<T | ApiEnvelope<T>>(endpoint, data, config);
  return unwrapApiResponse(response.data);
}

export async function apiPatch<T>(endpoint: string, data?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.patch<T | ApiEnvelope<T>>(endpoint, data);
  return unwrapApiResponse(response.data);
}

export async function apiDelete<T>(endpoint: string, data?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.delete<T | ApiEnvelope<T>>(endpoint, { data });
  return unwrapApiResponse(response.data);
}

export async function apiGetBlob(endpoint: string, params?: Record<string, unknown>): Promise<Blob> {
  const response = await apiClient.get(endpoint, {
    params,
    responseType: 'blob'
  });
  return response.data;
}

export async function apiPostFormData<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const response = await apiClient.post<T | ApiEnvelope<T>>(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return unwrapApiResponse(response.data);
}
